import { Model } from "@vuex-orm/core";
import type { Fields } from "@vuex-orm/core";
import type { Config } from "@vuex-orm/plugin-axios";

// eslint-disable-next-line import/no-cycle
import Video from "./Video";

export interface TranscriptSegment {
  id: string;
  start: number;
  end: number;
  text: string;
}
export interface Transcript {
  language: string;
  text: string;
  duration: number;
  segments: TranscriptSegment[];
}

export interface CombinedSegment {
  ids: string[];
  start: number;
  end: number;
  text: string;
}

export type NarrationStatus =
  | "pending"
  | "scheduled"
  | "transcribed"
  | "combined-transcript-segments"
  | "refined-transcript-segments"
  | "generated-suggestions"
  | "completed";

export default class Recording extends Model {
  // This is the name used as module name of the Vuex Store.
  public static entity = "recordings";

  public static get apiConfig(): Config {
    return {
      // headers: { 'X-Requested-With': 'XMLHttpRequest' },
      baseURL: "/api/v1/recordings",
    };
  }

  // List of all fields (schema) of the post model. `this.attr` is used
  // for the generic field type. The argument is the default value.
  public static fields(): Fields {
    return {
      id: this.number(-1),
      createdAt: this.string(""),
      initialUrl: this.string(""),
      videoId: this.number(null).nullable(),
      video: this.belongsTo(Video, "videoId"),

      cookies: this.attr(null),
      indexedDatabases: this.attr(null),
      localStorageDumps: this.attr([]),
      sessionStorageDumps: this.attr([]),

      url: this.string(""),
      eventsUrl: this.string(""),
      originalEventsUrl: this.string(""),
      redirectUrls: this.attr({}),

      stoppedAt: this.attr(null),
      completedProcessingAt: this.attr(null),
      processedNarrationAt: this.attr(null),
      narrationStatus: this.attr(null),

      narrationUrl: this.string(null).nullable(),
      transcript: this.attr(null),
      combinedTranscriptSegments: this.attr(null),
      refinedTranscriptSegments: this.attr(null),

      openingTitleSuggestions: this.attr([]),
      closingTitleSuggestions: this.attr([]),
      callToActionSuggestions: this.attr([]),
      callToActionUrl: this.string(null).nullable(),
    };
  }

  public declare id: number;

  public declare createdAt: string | null;

  public declare initialUrl: string;

  public declare videoId: number | null;

  public declare video: Video;

  public declare cookies: Record<string, unknown> | null;

  public declare indexedDatabases: Record<string, unknown> | null;

  // TODO: add proper types instead of Record<string, unknown>
  public declare localStorageDumps: Record<string, unknown>[];

  // TODO: add proper types instead of Record<string, unknown>
  public declare sessionStorageDumps: Record<string, unknown>[];

  public declare url: string;

  public declare eventsUrl: string;

  public declare originalEventsUrl: string;

  // TODO: add proper types instead of Record<string, unknown>
  public declare redirectUrls: Record<string, unknown>;

  public declare stoppedAt: string | null;

  public declare completedProcessingAt: string | null;

  public declare processedNarrationAt: string | null;

  public declare narrationStatus: NarrationStatus | null;

  public declare narrationUrl: string | null;

  public declare transcript: Transcript | null;

  public declare combinedTranscriptSegments: CombinedSegment[] | null;

  public declare refinedTranscriptSegments: CombinedSegment[] | null;

  public declare openingTitleSuggestions: string[];

  public declare closingTitleSuggestions: string[];

  public declare callToActionSuggestions: string[];

  public declare callToActionUrl: string | null;

  // static async saveClientDataFromWebContents(id, webContents) {
  //   const recording = this.find(id);
  //   const clientData = await this.getClientDataFromWebContents(webContents);
  //   console.log("Recording#saveClientDataFromWebContents got clientData", clientData);
  //   // The below update/$update have no effect in this async context, and thefore the whole
  //   // approach of updating the client data from within a class method (or an instance method for
  //   // that matter), actually does NOT work :,-(
  //   Recording.update({ where: recording.id, data: { clientData } });
  //   recording.$update(clientData);
  //   console.log("Recording#saveClientDataFromWebContents recording.$toJson()", recording.$toJson());
  //   const result = await Recording.api().patch(recording.id, { recording: recording.$toJson() });
  //   console.log("Recording#saveClientDataFromWebContents result", result);
  //   return clientData;
  // }
}
